import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton, Typography } from '@mui/material';
import { useMemo } from 'react';
import type { ChangeEvent } from 'react';
import { Button, Input, ToggleButtonGroup, TrashIcon } from '@/shared/ui';
import type { StringFilterParams } from '../../model/types';
import { StringFilterSelectionType } from '../../model/types';
import { FilterSelectionTypeSelect } from '../FilterSelectionTypeSelect/FilterSelectionTypeSelect';
import { useFiltersParamsContext } from '../FiltersParamsContext/FiltersParamsContext';

export enum ContainsOperator {
  AND = 'and',
  OR = 'or',
}

const OPTIONS: { label: string; value: ContainsOperator }[] = [
  {
    label: 'And',
    value: ContainsOperator.AND,
  },
  {
    label: 'Or',
    value: ContainsOperator.OR,
  },
];

export type ContainsValue =
  | {
      type: StringFilterSelectionType.CONTAINS;
      matchingStrings: string[];
      operator: ContainsOperator;
    }
  | {
      type: StringFilterSelectionType.NOT_CONTAIN;
      matchingStrings: string[];
      operator: ContainsOperator;
    };

interface Props<T> {
  id: string;
  value?: ContainsValue;
  onChange: (v: T) => void;
  getOptionValue: (option: unknown) => string;
}

export function ContainsStringFilterCortrol<T>({
  id,
  value,
  onChange,
  getOptionValue,
}: Props<T>) {
  const { paramsData } = useFiltersParamsContext();

  const filterParamsLoadable = paramsData[id];
  const allOptions = useMemo(
    () =>
      (filterParamsLoadable?.params as StringFilterParams | undefined)
        ?.options || [],
    [filterParamsLoadable],
  );
  const matchingStringsCount = useMemo(() => {
    const containsCount = allOptions.reduce((acc, option) => {
      const preparedOption = getOptionValue(option).toLowerCase();

      if (value?.operator === ContainsOperator.AND) {
        acc += value?.matchingStrings.every((matchingString) => {
          const preparedMatchingString = matchingString.trim().toLowerCase();

          return preparedOption.includes(preparedMatchingString);
        })
          ? 1
          : 0;
      } else if (value?.operator === ContainsOperator.OR) {
        acc += value?.matchingStrings.some((matchingString) => {
          const preparedMatchingString = matchingString.trim().toLowerCase();

          return preparedOption.includes(preparedMatchingString);
        })
          ? 1
          : 0;
      }

      return acc;
    }, 0);
    return value?.type === StringFilterSelectionType.CONTAINS
      ? containsCount
      : allOptions.length - containsCount;
  }, [value, allOptions, getOptionValue]);

  const handleTypeChange = (type: StringFilterSelectionType) => {
    if (
      type === StringFilterSelectionType.CONTAINS ||
      type === StringFilterSelectionType.NOT_CONTAIN
    ) {
      onChange({
        type,
        matchingStrings: [],
        operator: ContainsOperator.AND,
      } as T);
    } else {
      onChange({
        type,
        options: allOptions,
      } as T);
    }
  };
  const handleOperatorChange = (operator: ContainsOperator) => {
    onChange({
      type: value?.type || StringFilterSelectionType.CONTAINS,
      matchingStrings: value?.matchingStrings || [],
      operator,
    } as T);
  };
  const handleMatchingStringsChange = (matchingStrings: string[]) => {
    onChange({
      type: value?.type || StringFilterSelectionType.CONTAINS,
      matchingStrings,
      operator: value?.operator || ContainsOperator.AND,
    } as T);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
      <FilterSelectionTypeSelect
        value={value?.type}
        onChange={handleTypeChange}
      />
      <ToggleButtonGroup
        fullWidth
        options={OPTIONS}
        value={value?.operator}
        onChange={handleOperatorChange}
      />
      <MatchingStringsList
        value={value?.matchingStrings}
        onChange={handleMatchingStringsChange}
      />
      <Typography variant="caption">
        {matchingStringsCount} option{matchingStringsCount === 1 ? '' : 's'}{' '}
        matched
      </Typography>
    </Box>
  );
}

interface MatchingStringsListProps {
  value?: string[];
  onChange: (matchingStrings: string[]) => void;
}

function MatchingStringsList({ value, onChange }: MatchingStringsListProps) {
  const internalValue: string[] = !value || value.length === 0 ? [''] : value;

  const handleMatchingStringChange =
    (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      onChange(
        internalValue.map((matchingString, i) =>
          i === index ? event.target.value : matchingString,
        ),
      );
    };
  const handleMatchingStringDelete = (index: number) => () => {
    onChange(internalValue.filter((_, i) => i !== index));
  };
  const handleMatchingStringAdd = () => {
    onChange([...internalValue, '']);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {internalValue.map((matchingString, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Input
            placeholder="String to include"
            value={matchingString}
            onChange={handleMatchingStringChange(index)}
          />
          {index > 0 && (
            <IconButton
              size="small"
              onClick={handleMatchingStringDelete(index)}
            >
              <TrashIcon />
            </IconButton>
          )}
        </Box>
      ))}
      <Button
        sx={{ borderRadius: 2, py: 1 }}
        fullWidth
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleMatchingStringAdd}
      >
        Add string to include
      </Button>
    </Box>
  );
}
